'use client'

import { useQuery } from '@tanstack/react-query'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useMemo } from 'react'
import { useTokenActions } from '@/src/hooks/token-info/use-token-actions'
import { useAmplitudeAnalytics } from '@/src/hooks/use-amplitude-analytics'
import { tokenAcrossChainsQuery } from '@/src/queries/keys/tokens'
import { Button } from '../../ui/button'
import { Skeleton } from '../../ui/skeleton'
import TokenImg from '../../ui/token-img'
import { OSMO_ON_NAMADA, SOL_ON_SOLANA, USDC_ON_NAMADA } from './constants'

export default function CTA() {
  const router = useRouter()
  const { ampli } = useAmplitudeAnalytics()

  const { data: usdcTokenAcrossChains, isLoading: isLoadingUsdc } = useQuery(
    tokenAcrossChainsQuery(USDC_ON_NAMADA.chain, USDC_ON_NAMADA.denom),
  )

  const { data: osmoTokenAcrossChains, isLoading: isLoadingOsmo } = useQuery(
    tokenAcrossChainsQuery(OSMO_ON_NAMADA.chain, OSMO_ON_NAMADA.denom),
  )

  const { data: solTokenAcrossChains, isLoading: isLoadingSol } = useQuery(
    tokenAcrossChainsQuery(SOL_ON_SOLANA.chain, SOL_ON_SOLANA.denom),
  )

  const { getTokenInfoUrl } = useTokenActions()

  const isLoading = isLoadingUsdc || isLoadingOsmo || isLoadingSol

  const displayAssets = useMemo(() => {
    if (isLoading) return null

    const assets = [
      usdcTokenAcrossChains?.requested_asset,
      osmoTokenAcrossChains?.requested_asset,
      solTokenAcrossChains?.requested_asset,
    ].filter(Boolean)

    return assets
  }, [
    isLoading,
    usdcTokenAcrossChains?.requested_asset,
    osmoTokenAcrossChains?.requested_asset,
    solTokenAcrossChains?.requested_asset,
  ])

  if (
    !isLoading &&
    (!usdcTokenAcrossChains || !osmoTokenAcrossChains || !solTokenAcrossChains)
  )
    return null

  return (
    <div className='flex flex-row gap-3 flex-wrap justify-center'>
      {displayAssets === null ? (
        <>
          <Skeleton className='w-40 h-12 rounded-full' />
          <Skeleton className='w-40 h-12 rounded-full' />
          <Skeleton className='w-40 h-12 rounded-full' />
        </>
      ) : (
        displayAssets.map((asset) => {
          if (!asset) return null

          const url = getTokenInfoUrl({
            chain_id: asset.chain_id,
            denom: asset.denom,
            ticker: asset.ticker,
            direction: 'to',
          })

          if (url === undefined) return null

          return (
            <Button
              key={`cta-${asset.denom}-${asset.chain_id}`}
              variant='outline'
              className='text-primary text-base'
              asChild
            >
              <Link
                href={url}
                prefetch={true}
                onClick={() => {
                  if (asset) {
                    ampli?.pageInteraction({
                      page: 'home',
                      key: 'click on cta below search',
                      context: asset.ticker,
                    })
                  }
                }}
                onMouseEnter={() => {
                  router.prefetch(url)
                }}
              >
                <TokenImg
                  width={24}
                  height={24}
                  src={asset.kind_logo_img_url}
                  loading='eager'
                  priority={true}
                />
                Trade {asset.ticker}
              </Link>
            </Button>
          )
        })
      )}
    </div>
  )
}
